/* =============================================================
   Fish Fin Farms — custom.js
   Tiny enhancements only. CSS handles all layout / visibility.
   Loaded after Horizon's base scripts via theme.liquid (defer).
   ============================================================= */

document.addEventListener('DOMContentLoaded', function () {
  initStickyAtc();
  initSubscriptionToggle();
  initVariantPicker();
  initSubscriptionPicker();
  initHorizonStickyPriceSync();
});

/* ---------- 1. Sticky ATC — variant + selling-plan price sync ----------
   Visibility is CSS-only (always visible on mobile via the section's stylesheet).
   This script only updates the displayed price + variant chip when the user
   changes the variant or selling plan in the main product form.
   Guardrail: skips entirely when no .fff-sticky-atc element exists.
*/
function initStickyAtc() {
  const bar = document.querySelector('.fff-sticky-atc');
  if (!bar) return;

  const productForm = document.querySelector('product-form-component, product-form, form[action*="/cart/add"]');
  const priceNode = bar.querySelector('[data-fff-sticky-price]');
  const variantNameNode = bar.querySelector('[data-fff-variant-name]');
  const stickyButton = bar.querySelector('[data-fff-sticky-atc]');

  /* Read inline server-rendered data — no fetch round-trip on first variant change */
  const dataNode = bar.querySelector('[data-fff-sticky-data]');
  let stickyData = null;
  if (dataNode) {
    try { stickyData = JSON.parse(dataNode.textContent); } catch (e) { stickyData = null; }
  }
  if (!stickyData || !stickyData.variants) return;

  function getCurrentSellingPlanId() {
    /* Prefer our custom subscription picker's checked radio, then any name="selling_plan" */
    const fffSub = document.querySelector('[data-fff-sub-radio]:checked');
    if (fffSub && fffSub.value) return fffSub.value;
    const sp = document.querySelector('[name="selling_plan"]:checked, select[name="selling_plan"]');
    return sp && sp.value ? sp.value : null;
  }

  function getCurrentVariantId() {
    /* Prefer our custom variant picker's checked tile (carries data-variant-id),
       then any radio named "id", then a select. */
    const fffOpt = document.querySelector('[data-fff-vopt] [data-fff-vinput]:checked');
    if (fffOpt) {
      const wrapper = fffOpt.closest('[data-fff-vopt]');
      if (wrapper && wrapper.dataset.variantId) return wrapper.dataset.variantId;
    }
    const v = document.querySelector('[name="id"]:checked, select[name="id"], [name="id"]');
    return v ? v.value : null;
  }

  function syncBar() {
    const variantId = getCurrentVariantId();
    if (!variantId) return;
    const variant = stickyData.variants[String(variantId)];
    if (!variant) return;

    let displayPrice = variant.price;
    const sellingPlanId = getCurrentSellingPlanId();
    if (sellingPlanId && variant.allocations && variant.allocations[sellingPlanId] != null) {
      displayPrice = variant.allocations[sellingPlanId];
    }

    if (priceNode) priceNode.textContent = formatMoney(displayPrice);
    if (variantNameNode && variant.title) variantNameNode.textContent = variant.title;
    if (stickyButton) stickyButton.disabled = variant.available === false;
  }

  /* Listen for variant + selling-plan changes from any source:
     - our custom variant picker radios ([data-fff-vinput])
     - our custom subscription picker radios ([data-fff-sub-radio])
     - native Horizon variant-picker radios
     - any name="id"/name="selling_plan" inputs */
  document.addEventListener('change', function (event) {
    const t = event.target;
    if (!t || !t.matches) return;
    if (t.matches(
      '[data-fff-vinput], ' +
      '[data-fff-sub-radio], ' +
      '[name="id"], ' +
      '[name="selling_plan"], ' +
      'variant-picker select, ' +
      'variant-picker input[type="radio"]'
    )) {
      syncBar();
    }
  });

  /* Initial sync — pick up server-rendered selected state */
  syncBar();

  /* Sticky button is a projection of the main product form — submit it. */
  if (stickyButton && productForm) {
    stickyButton.addEventListener('click', function (e) {
      e.preventDefault();
      if (typeof productForm.requestSubmit === 'function') {
        productForm.requestSubmit();
      } else {
        productForm.submit();
      }
    });
  }
}

/* ---------- 2. Subscription pill toggle (size-grid section) ----------
   When the user toggles One-time / Subscribe:
   - Updates the visible price on each tile (price comes from data-fff-price-onetime / data-fff-price-sub)
   - Sets/clears the hidden selling_plan input on each tile's ATC form
   - Updates the strikethrough compare price visibility
*/
function initSubscriptionToggle() {
  document.querySelectorAll('.fff-sizes').forEach(function (section) {
    const toggle = section.querySelector('.fff-sizes__toggle');
    if (!toggle) return;

    const defaultPlanId = section.dataset.defaultSellingPlan || '';
    const opts = toggle.querySelectorAll('.fff-sizes__toggle-opt');

    function applyMode(mode) {
      opts.forEach(function (b) {
        const isActive = b.dataset.mode === mode;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      toggle.dataset.mode = mode;

      section.querySelectorAll('.fff-sizes__tile').forEach(function (tile) {
        const priceEl = tile.querySelector('.fff-sizes__price');
        const priceRow = tile.querySelector('[data-fff-price-row]');
        const planInput = tile.querySelector('[data-fff-selling-plan-input]');

        if (priceEl) {
          const target = mode === 'subscribe'
            ? priceEl.dataset.fffPriceSub
            : priceEl.dataset.fffPriceOnetime;
          if (target) priceEl.textContent = formatMoney(parseInt(target, 10));
        }

        if (priceRow) {
          priceRow.classList.toggle('is-onetime', mode === 'onetime');
        }

        if (planInput) {
          planInput.value = (mode === 'subscribe' && defaultPlanId) ? defaultPlanId : '';
        }
      });
    }

    opts.forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyMode(btn.dataset.mode || 'subscribe');
      });
    });

    /* Initialise to whichever pill is server-rendered as active. */
    const startActive = toggle.querySelector('.fff-sizes__toggle-opt.is-active');
    if (startActive) applyMode(startActive.dataset.mode || 'subscribe');
  });
}

/* Money formatting helper — works with Shopify's global if available, falls back. */
function formatMoney(cents) {
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    return window.Shopify.formatMoney(cents);
  }
  if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
  return '$' + (cents / 100).toFixed(2);
}

/* ---------- 3. Custom variant picker bridge ----------
   My fff-vpicker radios are visual only (name="fff-vpicker-{id}", not "id").
   Horizon's stock variant-picker is hidden via CSS but its radios are still
   in the DOM. When my radio changes, find Horizon's matching radio (matched
   on data-option1/2/3 → option value) and click it. Horizon then dispatches
   the full variant:selected → variant:update event chain natively.

   Bidirectional sync: when a variant changes via any other source and
   Horizon emits variant:update, sync my radio to reflect the new active variant.
*/
function initVariantPicker() {
  const pickers = document.querySelectorAll('[data-fff-vpicker]');
  if (!pickers.length) return;

  pickers.forEach(function (picker) {
    const horizonPicker = document.querySelector('variant-picker');

    function findHorizonRadiosForOptions(option1, option2, option3) {
      if (!horizonPicker) return [];
      const radios = horizonPicker.querySelectorAll('input[type="radio"]');
      const wanted = [option1, option2, option3].filter(Boolean).map(String);
      const matches = [];
      radios.forEach(function (r) {
        if (wanted.includes(String(r.value))) matches.push(r);
      });
      return matches;
    }

    picker.addEventListener('change', function (e) {
      const radio = e.target;
      if (!radio.matches('[data-fff-vinput]')) return;
      const opt = radio.closest('[data-fff-vopt]');
      if (!opt) return;
      const o1 = opt.dataset.option1 || '';
      const o2 = opt.dataset.option2 || '';
      const o3 = opt.dataset.option3 || '';
      const horizonRadios = findHorizonRadiosForOptions(o1, o2, o3);
      horizonRadios.forEach(function (hr) {
        if (!hr.checked) {
          hr.checked = true;
          hr.dispatchEvent(new Event('change', { bubbles: true }));
          hr.click();
        }
      });
    });

    document.addEventListener('variant:update', function (e) {
      const v = e.detail && e.detail.resource;
      if (!v) return;
      picker.querySelectorAll('[data-fff-vopt]').forEach(function (opt) {
        const isMatch = String(opt.dataset.variantId) === String(v.id);
        const input = opt.querySelector('[data-fff-vinput]');
        if (input) input.checked = isMatch;
      });
    });
  });
}

/* ---------- 4. Subscription picker variant-sync ----------
   Reads inline server-rendered variant + selling-plan price data, so price
   updates are instant — no /products/{handle}.js fetch round-trip.
   Also briefly buffers the prices and locks the ATC button while the
   change settles, as a guardrail against rapid-fire clicks.
*/
function initSubscriptionPicker() {
  const pickers = document.querySelectorAll('[data-fff-sub-picker]');
  if (!pickers.length) return;

  function syncToCartForm(value) {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    let input = form.querySelector('input[name="selling_plan"][data-fff-sync]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.dataset.fffSync = '1';
      form.appendChild(input);
    }
    input.value = value || '';
  }

  function readInlineData(picker) {
    const node = picker.querySelector('[data-fff-sub-data]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); }
    catch (e) { return null; }
  }

  pickers.forEach(function (picker) {
    const data = readInlineData(picker);
    if (!data || !data.variants) return;

    /* On radio change, mirror to a hidden input inside the cart form */
    picker.addEventListener('change', function (e) {
      if (!e.target.matches('[data-fff-sub-radio]')) return;
      syncToCartForm(e.target.value);
    });

    /* Initial sync — pick up whichever radio is server-rendered as checked */
    const checkedRadio = picker.querySelector('[data-fff-sub-radio]:checked');
    if (checkedRadio) syncToCartForm(checkedRadio.value);

    function syncToVariant(variantId) {
      const variant = data.variants[String(variantId)];
      if (!variant) return;

      const onetimePriceEl = picker.querySelector('[data-fff-sub-onetime-price] .fff-sub-picker__price-main');
      if (onetimePriceEl) onetimePriceEl.textContent = formatMoney(variant.price);

      picker.querySelectorAll('[data-fff-sub-plan-price]').forEach(function (priceEl) {
        const planId = priceEl.dataset.fffSubPlanPrice;
        const allocPrice = variant.allocations && variant.allocations[planId];
        const subPrice = (allocPrice != null) ? allocPrice : variant.price;

        priceEl.dataset.fffSubBase = variant.price;
        priceEl.dataset.fffSubDiscounted = subPrice;

        const main = priceEl.querySelector('.fff-sub-picker__price-main');
        const strike = priceEl.querySelector('.fff-sub-picker__strike');
        if (main) main.textContent = formatMoney(subPrice);
        if (strike) strike.textContent = formatMoney(variant.price);

        const opt = priceEl.closest('[data-fff-plan-id]');
        const badge = opt ? opt.querySelector('.fff-sub-picker__badge') : null;
        if (badge) {
          if (subPrice < variant.price) {
            const saved = variant.price - subPrice;
            const pct = Math.round((saved / variant.price) * 100);
            badge.textContent = '−' + pct + '%';
            badge.style.display = '';
          } else {
            badge.style.display = 'none';
          }
        }
      });

      picker.dataset.currentVariantId = variantId;
    }

    /* Pre-empt Horizon's section fetch: when the user clicks a custom variant
       tile, our picker dispatches change with the variant id in dataset.variantId.
       We sync prices here synchronously so the user sees the new price immediately. */
    document.addEventListener('change', function (e) {
      const opt = e.target && e.target.closest && e.target.closest('[data-fff-vopt]');
      if (!opt) return;
      const vid = opt.dataset.variantId;
      if (vid) {
        flashUpdate(picker);
        syncToVariant(vid);
      }
    });

    /* Late-binding fallback: also listen for Horizon's variant:update so
       any other variant-change source (URL change, programmatic) stays in sync. */
    document.addEventListener('variant:update', function (e) {
      const v = e.detail && e.detail.resource;
      if (v && v.id) syncToVariant(v.id);
    });
  });
}

/* ---------- 5. Selling-plan price sync to Horizon's native sticky ATC ----------
   Horizon's <sticky-add-to-cart> morphs its content on variant change but does
   NOT re-render on selling-plan-only changes — so toggling Subscribe leaves
   the bar showing the variant's regular price. We patch the visible price
   text whenever the active variant or selling plan changes, using the same
   inline data the subscription picker was built on (no extra fetch). */
function initHorizonStickyPriceSync() {
  const sticky = document.querySelector('sticky-add-to-cart');
  if (!sticky) return;

  const subPicker = document.querySelector('[data-fff-sub-picker]');
  if (!subPicker) return;

  const dataNode = subPicker.querySelector('[data-fff-sub-data]');
  if (!dataNode) return;
  let data;
  try { data = JSON.parse(dataNode.textContent); } catch (e) { return; }
  if (!data || !data.variants) return;

  function getCurrentVariantId() {
    const fffOpt = document.querySelector('[data-fff-vopt] [data-fff-vinput]:checked');
    if (fffOpt) {
      const wrapper = fffOpt.closest('[data-fff-vopt]');
      if (wrapper && wrapper.dataset.variantId) return wrapper.dataset.variantId;
    }
    return sticky.dataset.currentVariantId || null;
  }

  function getCurrentSellingPlanId() {
    const fffSub = document.querySelector('[data-fff-sub-radio]:checked');
    if (fffSub && fffSub.value) return fffSub.value;
    return null;
  }

  function applyPrice() {
    const vid = getCurrentVariantId();
    if (!vid) return;
    const variant = data.variants[String(vid)];
    if (!variant) return;
    let price = variant.price;
    const spId = getCurrentSellingPlanId();
    if (spId && variant.allocations && variant.allocations[spId] != null) {
      price = variant.allocations[spId];
    }
    /* Update both the regular-state span and the sale-state span. Horizon
       toggles their visibility via .price__hidden — overwriting the hidden
       one is harmless. */
    sticky.querySelectorAll('.sticky-add-to-cart__price .price, .sticky-add-to-cart__price .price-item--sale')
      .forEach(function (n) { n.textContent = formatMoney(price); });
  }

  /* Re-apply on user interactions and after Horizon's morph completes.
     Defer one frame so morph (if it ran) has settled. */
  function deferApply() { setTimeout(applyPrice, 16); }

  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.matches) return;
    if (e.target.matches('[data-fff-sub-radio], [data-fff-vinput]')) deferApply();
  });
  document.addEventListener('variant:update', deferApply);

  /* Initial sync — page may have loaded with ?selling_plan in URL. */
  applyPrice();
}

/* ---------- 6. Update buffer + ATC lock ----------
   Adds a brief is-updating class for a CSS opacity dip (so users see the
   change), and disables the ATC button for ~250 ms as a guardrail against
   rapid-fire clicks during a variant transition.
*/
function flashUpdate(picker) {
  if (!picker || picker.classList.contains('is-updating')) return;
  picker.classList.add('is-updating');
  setTimeout(function () { picker.classList.remove('is-updating'); }, 200);

  const atcs = document.querySelectorAll(
    'add-to-cart-component button, ' +
    'product-form-component button[type="submit"], ' +
    'form[action*="/cart/add"] button[type="submit"], ' +
    '[data-fff-sticky-atc]'
  );
  atcs.forEach(function (btn) {
    if (btn.disabled) return;
    btn.disabled = true;
    btn.dataset.fffLocked = '1';
  });
  setTimeout(function () {
    atcs.forEach(function (btn) {
      if (btn.dataset.fffLocked === '1') {
        btn.disabled = false;
        delete btn.dataset.fffLocked;
      }
    });
  }, 250);
}
